import React, { lazy, Suspense, useCallback, useState } from "react";
import { useMount } from "react-use";
import { Banner } from "@atlaskit/page-layout";
import { ErrorBoundary } from "@sentry/react";

import { useResizeObserver } from "fond/utils/hooks";
import localStorage from "fond/utils/localStorage";

import { useBanners } from "./useBanners";

const NotificationBanner = lazy(() => import("./NotificationBanner"));

type NotificationBannerSlotProps = {
  id?: string;
  isFixed?: boolean;
};

const STORAGE_KEY = "FOND_BANNER_SLOT_HEIGHT";
const ATLASKIT_DEFAULT_BANNER_HEIGHT = 56;

export const NotificationBannerSlot: React.FC<NotificationBannerSlotProps> = ({ id, isFixed = true }: NotificationBannerSlotProps) => {
  const { banners, isLoading, dismiss } = useBanners();
  const bannerSlotRef = React.useRef<HTMLDivElement>(null);
  const initialHeight = parseInt(localStorage.getItem(STORAGE_KEY, ATLASKIT_DEFAULT_BANNER_HEIGHT), 10);
  const [bannerSlotHeight, setBannerSlotHeight] = useState<number>(initialHeight);

  /**
   * Updates the height of the banner slot based on the current height of the banners displayed.
   * This height is also stored in localStorage to persist across page reloads and avoid unnecessary
   * recalculations and content flickering.
   */
  const updateBannerSlotHeight = useCallback(() => {
    const height = bannerSlotRef.current?.clientHeight ?? initialHeight;
    setBannerSlotHeight(height);
    localStorage.setItem(STORAGE_KEY, height.toString());
  }, [initialHeight]);

  /**
   * Determines the required height for all displayed banners
   */
  useResizeObserver(bannerSlotRef, updateBannerSlotHeight);
  useMount(updateBannerSlotHeight);

  if (isLoading || !banners?.length) {
    return null;
  }

  return (
    <ErrorBoundary>
      <Banner id={id} height={bannerSlotHeight} isFixed={isFixed}>
        <div className="notification-banner-slot" ref={bannerSlotRef}>
          <Suspense fallback={null}>
            {banners.map((banner) => (
              <NotificationBanner key={banner.ID} id={banner.ID} banner={banner} onDismiss={() => dismiss(banner)} />
            ))}
          </Suspense>
        </div>
      </Banner>
    </ErrorBoundary>
  );
};
